/**
 * @fileoverview Dynamic SEO Metadata Component
 * 
 * This component dynamically updates metadata tags in the document head
 * based on the current language. It works with the language context to
 * ensure proper localization of titles, descriptions, and OpenGraph tags.
 */

import React, { useEffect, useState } from 'react';
import { useLanguage } from '@/lib/language-context';
import { LocaleType } from '@/types/chat';

// Localized metadata for each supported language
const localizedMetadata: Record<LocaleType, {
  title: string;
  description: string;
  keywords: string;
}> = {
  // Arabic translations
  ar: {
    title: "Mila - خبيرة قوانين وزارة التجارة السعودية | MJ",
    description: "مساعد ذكاء اصطناعي لقوانين وأنظمة وزارة التجارة السعودية. احصل على إرشادات فورية حول قوانين الشركات، والوكالات التجارية، ومكافحة الغش التجاري، والأنظمة التجارية.",
    keywords: "وزارة التجارة، قانون الشركات، المملكة العربية السعودية، نظام السجل التجاري، نظام الأسماء التجارية، نظام الإفلاس، نظام المهن الهندسية"
  },

  // English translations
  en: {
    title: "Mila - Saudi Ministry of Commerce Laws Expert | MJ",
    description: "AI-powered assistant for Saudi Ministry of Commerce laws and regulations. Get instant guidance on companies law, commercial agencies, fraud prevention, and commercial regulations.",
    keywords: "Ministry of Commerce, companies law, Saudi Arabia, commercial register, trade names, insolvency law, engineering professions law"
  },

  // Chinese translations
  zh: {
    title: "Mila - 沙特商务部法律专家 | MJ",
    description: "为沙特阿拉伯商务部法律法规而设计的人工智能助手。获取关于公司法、商业代理、欺诈防范和商业法规的即时指导。",
    keywords: "商务部, 公司法, 沙特阿拉伯, 商业登记, 商标名称, 破产法, 工程专业法"
  },

  // Japanese translations
  ja: {
    title: "Mila - サウジ商務省法律専門家 | MJ",
    description: "サウジアラビア商務省の法律・規制に関するAI支援アシスタント。企業法、商業代理店、詐欺防止、商業規制に関する即時ガイダンスを取得します。",
    keywords: "商務省、企業法、サウジアラビア、商業登録、商号、破産法、エンジニアリング職業法"
  },

  // Russian translations
  ru: {
    title: "Mila - Эксперт по законам Министерства торговли Саудовской Аравии | MJ",
    description: "ИИ-помощник по законам и нормативным актам Министерства торговли Саудовской Аравии. Получите мгновенные рекомендации по корпоративному праву, коммерческим агентствам, предотвращению мошенничества и коммерческим нормам.",
    keywords: "Министерство торговли, корпоративное право, Саудовская Аравия, торговый реестр, торговые наименования, закон о несостоятельности, закон об инженерных профессиях"
  },

  // German translations
  de: {
    title: "Mila - Expertin für Gesetze des saudi-arabischen Handelsministeriums | MJ",
    description: "KI-gestützter Assistent für Gesetze und Vorschriften des saudi-arabischen Handelsministeriums. Erhalten Sie sofortige Anleitungen zu Gesellschaftsrecht, Handelsvertretungen, Betrugsprävention und Handelsvorschriften.",
    keywords: "Handelsministerium, Gesellschaftsrecht, Saudi-Arabien, Handelsregister, Handelsnamen, Insolvenzrecht, Ingenieurberufegesetz"
  },

  // French translations
  fr: {
    title: "Mila - Expert en lois du ministère du Commerce saoudien | MJ",
    description: "Assistant alimenté par l'IA pour les lois et réglementations du ministère du Commerce saoudien. Obtenez des conseils instantanés sur le droit des sociétés, les agences commerciales, la prévention de la fraude et les réglementations commerciales.",
    keywords: "Ministère du Commerce, droit des sociétés, Arabie Saoudite, registre du commerce, noms commerciaux, loi sur l'insolvabilité, loi sur les professions d'ingénierie"
  },

  // Spanish translations
  es: {
    title: "Mila - Experta en Leyes del Ministerio de Comercio Saudí | MJ",
    description: "Asistente con IA para leyes y regulaciones del Ministerio de Comercio saudí. Obtenga orientación instantánea sobre ley de empresas, agencias comerciales, prevención de fraude y regulaciones comerciales.",
    keywords: "Ministerio de Comercio, ley de empresas, Arabia Saudí, registro comercial, nombres comerciales, ley de insolvencia, ley de profesiones de ingeniería"
  },

  // Hindi translations
  hi: {
    title: "Mila - सऊदी वाणिज्य मंत्रालय कानून विशेषज्ञ | MJ",
    description: "सऊदी वाणिज्य मंत्रालय के कानूनों और नियमों के लिए AI-संचालित सहायक। कंपनी कानून, वाणिज्यिक एजेंसियों, धोखाधड़ी की रोकथाम, और वाणिज्यिक नियमों पर तत्काल मार्गदर्शन प्राप्त करें।",
    keywords: "वाणिज्य मंत्रालय, कंपनी कानून, सऊदी अरब, व्यापारिक रजिस्टर, व्यापारिक नाम, दिवालियापन कानून, इंजीनियरिंग पेशे कानून"
  },

  // Turkish translations
  tr: {
    title: "Mila - Suudi Ticaret Bakanlığı Kanunları Uzmanı | MJ",
    description: "Suudi Ticaret Bakanlığı kanunları ve düzenlemeleri için yapay zeka destekli asistan. Şirketler hukuku, ticari acenteler, dolandırıcılık önleme ve ticari düzenlemeler hakkında anında rehberlik alın.",
    keywords: "Ticaret Bakanlığı, şirketler hukuku, Suudi Arabistan, ticaret sicili, ticari isimler, iflas hukuku, mühendislik meslekleri kanunu"
  },

  // Swedish translations
  sv: {
    title: "Mila - Expert på Saudiska Handelsdepartementets lagar | MJ",
    description: "AI-driven assistent för Saudiska Handelsdepartementets lagar och förordningar. Få omedelbar vägledning om bolagsrätt, handelsombud, bedrägeriförebyggande och handelsföreskrifter.",
    keywords: "Handelsdepartementet, bolagsrätt, Saudiarabien, handelsregister, handelsnamn, insolvensrätt, ingenjörsyrkeslag"
  }
};

/**
 * Updates SEO metadata in the document head based on the current language
 * @returns null - This component doesn't render any visible elements
 */
export function SEOMetadata(): null {
  const { locale, isRTL } = useLanguage();
  
  // Track URL path and search params for language changes
  const [pathAndSearch, setPathAndSearch] = React.useState(() => 
    typeof window !== 'undefined' ? window.location.pathname + window.location.search : '');
  
  // Set up listener for URL path and query changes
  React.useEffect(() => {
    const handleUrlChange = () => {
      // We need to track both pathname and search for path-based localization
      const currentPathWithQuery = window.location.pathname + window.location.search;
      if (pathAndSearch !== currentPathWithQuery) {
        setPathAndSearch(currentPathWithQuery);
      }
    };
    
    // Listen for navigation events
    window.addEventListener('popstate', handleUrlChange);
    window.addEventListener('hashchange', handleUrlChange);
    
    // Create a MutationObserver to watch for changes to content that might indicate navigation
    if (typeof MutationObserver !== 'undefined') {
      const observer = new MutationObserver(() => {
        handleUrlChange();
      });
      
      // Observe changes to the document head, which often changes during navigation
      observer.observe(document.head || document.documentElement, { childList: true, subtree: true });
    }
    
    // Set up periodic checks for SPA navigation where events might not fire
    const intervalId = setInterval(handleUrlChange, 1000);
    
    // Force an update on mount to ensure proper initialization
    handleUrlChange();
    
    return () => {
      window.removeEventListener('popstate', handleUrlChange);
      window.removeEventListener('hashchange', handleUrlChange);
      clearInterval(intervalId);
    };
  }, [pathAndSearch]);
  
  // Initialize with URL parameters
  useEffect(() => {
    // Check URL parameters on component mount
    const urlParams = new URLSearchParams(window.location.search);
    const initialLangParam = urlParams.get('lang');
    
    // Validate language param silently
    // Also check URL path for language code
    const pathParts = window.location.pathname.split('/').filter(Boolean);
    const pathLang = pathParts[0] as LocaleType | undefined;
    
    // Trigger a metadata update
    setPathAndSearch(window.location.pathname + window.location.search);
  }, []);

  // Update metadata when locale or URL parameters change
  useEffect(() => {
    try {
      // Check for language in URL path first (new preferred method)
      const pathParts = window.location.pathname.split('/').filter(Boolean);
      const pathLang = pathParts[0] as LocaleType | undefined;
      
      // Then check URL parameters as fallback (old method)
      const urlParams = new URLSearchParams(window.location.search);
      const queryLang = urlParams.get('lang') as LocaleType | null;
      
      // Prioritize path language over query parameter
      let detectedLang: LocaleType | null = null;
      
      if (pathLang && Object.keys(localizedMetadata).includes(pathLang)) {
        detectedLang = pathLang;
      } else if (queryLang && Object.keys(localizedMetadata).includes(queryLang)) {
        detectedLang = queryLang;
      }
      
      // Use detected language if available and valid, otherwise use context locale
      const effectiveLocale = detectedLang || locale;
      
      const metadata = localizedMetadata[effectiveLocale] || localizedMetadata.en;
      
      // Update document title
      document.title = metadata.title;
      
      // Update meta tags
      updateMetaTag('title', metadata.title);
      updateMetaTag('description', metadata.description);
      updateMetaTag('keywords', metadata.keywords);
      
      // Update Open Graph tags
      updateMetaTag('og:title', metadata.title, 'property');
      updateMetaTag('og:description', metadata.description, 'property');
      updateMetaTag('og:locale', effectiveLocale === 'en' ? 'en_US' : `${effectiveLocale}_${effectiveLocale.toUpperCase()}`, 'property');
      
      // Update Twitter Card tags
      updateMetaTag('twitter:title', metadata.title);
      updateMetaTag('twitter:description', metadata.description);
      
      // No logging needed for metadata updates
    } catch (error) {
      // Silent catch - metadata update is non-critical
    }
  }, [locale, isRTL, pathAndSearch]);
  
  // Helper function to find or create meta tags
  function updateMetaTag(name: string, content: string, attributeName: 'name' | 'property' = 'name') {
    // Look for existing tag
    let metaTag = document.querySelector(`meta[${attributeName}="${name}"]`);
    
    // Create tag if it doesn't exist
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.setAttribute(attributeName, name);
      document.head.appendChild(metaTag);
    }
    
    // Update content
    metaTag.setAttribute('content', content);
  }
  
  // This component doesn't render anything
  return null;
}

export default SEOMetadata;